import { useState } from "react";
import { Outlet } from "react-router-dom";
import { Alert, Box, Button } from "@mui/material";
import useResponsive from "../../hooks/useResponsive";
import { useSettingsContext } from "../../components/settings";
import Main from "./Main";
import Header from "./header";
import NavMini from "./nav/NavMini";
import NavVertical from "./nav/NavVertical";
import NavHorizontal from "./nav/NavHorizontal";
import { useAuthContext } from "src/auth/useAuthContext";
import { useDispatch } from "react-redux";
import { updateVenueOnlineStatus } from "src/redux/slices/venue";
import axios from "../../utils/axios";
import { useSnackbar } from "../../components/snackbar";

export default function DashboardLayout() {
  const { themeLayout } = useSettingsContext();
  const { user, logout } = useAuthContext();
  let dispatch = useDispatch();
  const isDesktop = useResponsive("up", "lg");
  const [open, setOpen] = useState(false);
  const [isEndingImpersonation, setIsEndingImpersonation] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const isNavHorizontal = themeLayout === "horizontal";
  const isNavMini = themeLayout === "mini";
  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleBackToAdmin = async () => {
    const adminAppUrl = process.env.REACT_APP_ADMIN_APP_URL;
    if (!adminAppUrl) {
      enqueueSnackbar("Admin portal URL is not configured", { variant: "error" });
      return;
    }
    if (isEndingImpersonation) return;

    setIsEndingImpersonation(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/authw/impersonation/end`);
    } catch (error) {
      // Local credentials are still removed if the server session already expired.
    } finally {
      logout();
      window.location.assign(
        new URL(user?.adminReturnPath || "/dashboard/franchise/list", adminAppUrl).toString()
      );
    }
  };

  const impersonationBanner = user?.isImpersonating ? (
    <Alert
      severity="warning"
      action={(
        <Button color="inherit" size="small" disabled={isEndingImpersonation} onClick={handleBackToAdmin}>
          Back to Admin
        </Button>
      )}
      sx={{ mb: 2 }}
    >
      Viewing as Egora Admin — {user?.warehouse?.name || user?.warehouseId?.name || "this warehouse"}
    </Alert>
  ) : null;

  const [isVenueOnline, setIsVenueOnline] = useState(user?.venue?.isOnline ?? false);

  const handleVenueOnlineStatusChange = async (e) => {
    const isChecked = e.target.checked;
    setIsVenueOnline(isChecked);
    await dispatch(updateVenueOnlineStatus(user.venueId, isChecked, user?.photoURL, user?.receiptURL));
  };

  const renderNavVertical = (
    <NavVertical
      openNav={open}
      onCloseNav={handleClose}
      isVenueOnline={isVenueOnline}
      handleVenueOnlineStatusChange={handleVenueOnlineStatusChange}
    />
  );

  if (isNavHorizontal) {
    return (
      <>
        <Header onOpenNav={handleOpen} />

        {isDesktop ? <NavHorizontal /> : renderNavVertical}

        <Main>
          {impersonationBanner}
          <Outlet />
        </Main>
      </>
    );
  }

  if (isNavMini) {
    return (
      <>
        <Header onOpenNav={handleOpen} />

        <Box
          sx={{
            display: { lg: "flex" },
            minHeight: { lg: 1 },
          }}
        >
          {isDesktop ? (
            <NavMini isVenueOnline={isVenueOnline} handleVenueOnlineStatusChange={handleVenueOnlineStatusChange} />
          ) : (
            renderNavVertical
          )}

          <Main>
            {impersonationBanner}
            <Outlet />
          </Main>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={handleOpen} />

      <Box
        sx={{
          display: { lg: "flex" },
          minHeight: { lg: 1 },
        }}
      >
        {renderNavVertical}

        <Main>
          {impersonationBanner}
          <Outlet />
        </Main>
      </Box>
    </>
  );
}
